import React from 'react';

// Panel de los eventos del directo.
//
// Un evento es un reto que salta como alerta en mitad del directo ("EVENTO: Titulo") y que
// hay que cumplir; la descripcion dice el que y solo se ve en el panel de recompensas. Salta
// en los directos del calendario que lo lleven (se eligen al publicar el directo), las veces
// que diga, en momentos al azar (ver apps/streamEvents.js). Mismo esquema que AdminDrops.

const EMPTY = { title: '', description: '', timesPerStream: 1 };

const formatTime = (iso) => (iso
    ? new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    : '');

const post = async (url, fields) => {
    const formData = new FormData();
    Object.keys(fields).forEach(key => { if (fields[key] !== undefined) formData.append(key, fields[key]); });
    const res = await fetch(url, { method: 'POST', body: formData });
    return res.json();
};

const AdminEventos = () => {
    const [form, setForm] = React.useState(EMPTY);
    const [image, setImage] = React.useState(null);
    const [editingId, setEditingId] = React.useState(null);
    const [state, setState] = React.useState(null);
    const [status, setStatus] = React.useState('');
    const [statusType, setStatusType] = React.useState(''); // 'success' | 'error' | ''
    const [loading, setLoading] = React.useState(false);
    const fileRef = React.useRef(null);

    const say = (text, type) => { setStatus(text); setStatusType(type); };

    const refresh = React.useCallback(async () => {
        try {
            setState(await post('/api/adminLogin?action=get&type=streamEvents', {}));
        } catch (err) {
            say('No se pudieron leer los eventos', 'error');
        }
    }, []);

    React.useEffect(() => { refresh(); }, [refresh]);

    const resetForm = () => {
        setForm(EMPTY);
        setImage(null);
        setEditingId(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    const handleImage = (e) => {
        const file = e.target.files && e.target.files[0];
        if (file && file.size > 2 * 1024 * 1024) {
            say('La imagen pasa de 2 MB', 'error');
            e.target.value = '';
            return;
        }
        setImage(file || null);
    };

    const handleSave = async () => {
        if (!form.title.trim()) return say('Ponle un título al evento', 'error');
        if (!editingId && !image) return say('Elige la imagen que saldrá en la alerta', 'error');
        setLoading(true);
        say('', '');
        try {
            const action = editingId ? 'edit' : 'create';
            const res = await post('/api/adminLogin?action=' + action + '&type=streamEvents', {
                id: editingId || undefined,
                title: form.title,
                description: form.description,
                timesPerStream: form.timesPerStream,
                image: image || undefined
            });
            if (res && res.success) {
                say(editingId ? 'Evento guardado.' : 'Evento "' + form.title.trim() + '" creado. Elígelo en el Calendario al publicar un directo.', 'success');
                resetForm();
                await refresh();
            } else {
                say((res && res.error) || 'No se pudo guardar el evento', 'error');
            }
        } catch (err) {
            say('No se pudo guardar el evento', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (event) => {
        setEditingId(event.id);
        setForm({ title: event.title, description: event.description || '', timesPerStream: event.timesPerStream || 1 });
        setImage(null);
        if (fileRef.current) fileRef.current.value = '';
        say('', '');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (event) => {
        if (!window.confirm('¿Borrar el evento "' + event.title + '"? Si tenía avisos programados ya no saltarán.')) return;
        const res = await post('/api/adminLogin?action=delete&type=streamEvents', { id: event.id }).catch(() => null);
        say(res && res.success ? 'Evento borrado.' : (res && res.error) || 'No se pudo borrar', res && res.success ? 'success' : 'error');
        if (editingId === event.id) resetForm();
        await refresh();
    };

    const handleControl = async (op, extra = {}) => {
        setLoading(true);
        say('', '');
        try {
            const res = await post('/api/adminLogin?action=streamEventsControl', Object.assign({ op: op }, extra));
            if (res && res.success) {
                if (op === 'start') {
                    say(res.scheduled > 0
                        ? res.scheduled + ' avisos repartidos por el directo "' + res.schedule + '".'
                        : res.schedule
                            ? 'El directo "' + res.schedule + '" no tiene eventos pendientes.'
                            : 'No hay ningún directo del calendario cerca de ahora.', res.scheduled > 0 ? 'success' : 'error');
                } else if (op === 'stop') {
                    say('Parado: los avisos que quedaban ya no saltarán en este directo.', 'success');
                } else if (op === 'test') {
                    say('Alerta de prueba enviada al overlay y al panel de recompensas. No se ha gastado nada.', 'success');
                } else {
                    say('Ha saltado "EVENTO: ' + res.event.title + '".', 'success');
                }
            } else {
                say((res && res.error) || 'No se pudo completar la operación', 'error');
            }
            await refresh();
        } catch (err) {
            say('Error al controlar los eventos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const events = (state && state.events) || [];
    const session = state && state.session;
    const upcoming = state && state.upcoming;

    return (
        <div className='heroes formOver'>
            <h1>Eventos</h1>
            <p style={{ fontSize: 13, color: '#ccc', fontFamily: 'Arial, Helvetica, sans-serif' }}>
                Un evento salta en mitad del directo como una recompensa: alerta en el overlay con su imagen y
                "EVENTO: título", y en el panel de recompensas con su descripción para que sepas qué hacer.
                Solo salta en los directos que lo lleven: elígelo en el <strong>Calendario</strong> al publicar el directo.
                Cae en momentos al azar entre la hora de inicio y la de fin del directo, las veces que digas.
            </p>

            <h2>{editingId ? 'Editar evento' : 'Nuevo evento'}</h2>
            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>Título (sale en la alerta)</label>
                <input className='userSearch' placeholder='Hablar con acento argentino' value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>Descripción (lo que hay que hacer)</label>
                <textarea className='userSearch' rows='3' placeholder='Durante 5 minutos hablo con acento argentino'
                    value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>Veces por directo</label>
                <input className='userSearch' type='number' min='1' max='20' value={form.timesPerStream}
                    onChange={(e) => setForm({ ...form, timesPerStream: e.target.value })}
                    onWheel={(e) => e.currentTarget.blur()} />
            </div>
            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>{editingId ? 'Imagen (vacía = la misma)' : 'Imagen de la alerta'}</label>
                <input className='userSearch' type='file' accept='image/*' ref={fileRef} onChange={handleImage} />
            </div>
            <div className='navTops heroes unselectable'>
                <div onClick={() => { if (!loading) handleSave(); }}>{editingId ? 'Guardar cambios' : 'Crear evento'}</div>
                {editingId ? <div onClick={() => { if (!loading) resetForm(); }}>Cancelar</div> : null}
            </div>

            <h2>Directo</h2>
            {!session ? <p>Cargando...</p> : (
                <p style={{ fontSize: 14 }}>
                    {session.running
                        ? 'En marcha' + (session.stream ? ' en "' + session.stream.title + '"' : '') + ': quedan ' +
                          session.scheduled + ' avisos' + (session.nextAt ? '. El próximo ("' + session.nextTitle + '") a las ' + formatTime(session.nextAt) : '') + '.'
                        : upcoming
                            ? 'Parado. Si el directo empezara ahora sería "' + upcoming.title + '" (' + formatTime(upcoming.start) + '–' +
                              formatTime(upcoming.end) + ') ' + (upcoming.events.length ? 'con: ' + upcoming.events.join(', ') : 'sin eventos') + '.'
                            : 'Parado. No hay ningún directo del calendario cerca de ahora.'}
                    {' Arranca solo al empezar el directo de Twitch.'}
                </p>
            )}
            <div className='navTops heroes unselectable'>
                <div onClick={() => { if (!loading) handleControl('start'); }}>Iniciar ahora</div>
                <div onClick={() => { if (!loading) handleControl('stop'); }}>Parar</div>
                <div onClick={() => { if (!loading) handleControl('fireNow'); }}>Lanzar el siguiente ya</div>
                <div onClick={() => { if (!loading) refresh(); }}>Actualizar</div>
            </div>

            <h2>Eventos</h2>
            {events.length === 0 ? <p>Todavía no hay ningún evento.</p> : events.map(event => (
                <div key={event.id} className='formFieldRow' style={{ alignItems: 'center' }}>
                    {event.image ? <img src={'/images/rewards/' + event.image} alt='' style={{ width: 56, height: 56, objectFit: 'contain', marginRight: 12 }} /> : null}
                    <span style={{ flex: 1 }}>
                        <strong>{event.title}</strong>
                        <span style={{ fontSize: 13, color: '#ccc' }}>{' · ' + event.timesPerStream + (event.timesPerStream === 1 ? ' vez' : ' veces') + ' por directo'}</span>
                        {event.description ? <><br /><span style={{ fontSize: 13, color: '#ccc' }}>{event.description}</span></> : null}
                    </span>
                    <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) handleEdit(event); }}>Editar</div>
                    <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) handleControl('test', { id: event.id }); }}>Probar alerta</div>
                    <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) handleDelete(event); }}>Borrar</div>
                </div>
            ))}

            {loading ? <p>Cargando...</p> : null}
            {status ? (
                <p
                    role={statusType === 'error' ? 'alert' : 'status'}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        fontWeight: 600,
                        color: statusType === 'success' ? '#0a6b0a' : statusType === 'error' ? '#8a1a1a' : 'inherit',
                        backgroundColor: statusType === 'success' ? '#d9f5d9' : statusType === 'error' ? '#fdecea' : 'transparent'
                    }}
                >
                    {status}
                </p>
            ) : null}
        </div>
    );
};

export default AdminEventos;
